import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { createApiClient } from '../../apiClient';
import { updateAuthFromVerifyResponse } from '../../auth/updateAuthFromVerifyResponse';
import { disconnectWalletAccount } from '../disconnectWalletAccount';
import { removeUnverifiedWalletAccount } from '../unverifiedWalletAccounts/removeUnverifiedWalletAccount';
import type { WalletAccount } from '../walletAccount';

type RemoveWalletAccountParams = {
  walletAccount: WalletAccount;
};

/**
 * Removes a wallet account from the current session or user (if authenticated).
 *
 * This function both disconnects and removes the specified wallet account,
 * updating local state and server records (if there is an authenticated user).
 * For verified wallets, it will unlink them from the user's account.
 *
 * @param params.walletAccount - The wallet account to remove.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves when the wallet account is successfully removed.
 */
export const removeWalletAccount = async (
  { walletAccount }: RemoveWalletAccountParams,
  client = getDefaultClient()
): Promise<void> => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  await disconnectWalletAccount({ walletAccount }, client);

  if (!walletAccount.verifiedCredentialId) {
    removeUnverifiedWalletAccount(
      { unverifiedWalletAccount: walletAccount },
      client
    );

    return;
  }

  const response = await apiClient.verifyUnlink({
    environmentId: core.environmentId,
    verifyUnlinkRequest: {
      walletId: walletAccount.verifiedCredentialId,
    },
  });

  updateAuthFromVerifyResponse({ response }, client);
};
